import { reloadApp } from '@/utils/app-reload';
import {
  BranchCookie,
  clearBranchCookie,
  readBranchCookie,
  writeBranchCookie,
} from '@/utils/branch-cookie';

/**
 * What the Labs icon shows.
 * - `none`: no icon
 * - `opted-in`: running the prototype ("Leave prototype")
 * - `opted-out`: eligible, on the standard UI ("Try it")
 */
export type LabsStatus = 'none' | 'opted-in' | 'opted-out';

export type AssignmentAction =
  | { type: 'none' }
  /** Assign the flag's branch and reload (subject to the loop guard). */
  | { type: 'assign'; branch: string }
  /** Clear the cookie and reload: the prototype has ended. */
  | { type: 'clear' };

export interface AssignmentDecision {
  status: LabsStatus;
  action: AssignmentAction;
}

const NOTHING: AssignmentDecision = { status: 'none', action: { type: 'none' } };

/**
 * Decides what to show and do, given the current cookie and the flag's branch.
 *
 * @param cookie - the decoded branch cookie, or undefined when there is none
 * @param flagBranch - the flag's branch (`''` means no prototype), or null when the flag value
 *   can't be trusted yet (anonymous context, LaunchDarkly failed, etc.). Null never acts, but a
 *   `labs` cookie still shows as opted-in so the user can leave.
 * @param alreadyRedirected - whether this session already auto-redirected to `flagBranch` (loop guard)
 */
export function decideAssignment(
  cookie: BranchCookie | undefined,
  flagBranch: string | null,
  alreadyRedirected: boolean
): AssignmentDecision {
  if (flagBranch === null) {
    // Don't act without a trustworthy flag value, but always offer a way off a prototype: it's
    // the user's escape hatch when LaunchDarkly is down or the prototype is broken.
    return cookie?.status === 'labs' ? { status: 'opted-in', action: { type: 'none' } } : NOTHING;
  }
  if (cookie?.status === 'discretionary') return NOTHING;

  if (cookie?.status === 'labsOptOut') {
    return flagBranch ? { status: 'opted-out', action: { type: 'none' } } : NOTHING;
  }

  if (cookie?.status === 'labs') {
    if (flagBranch === cookie.name) {
      return { status: 'opted-in', action: { type: 'none' } };
    }
    if (!flagBranch) return { status: 'none', action: { type: 'clear' } };
    // Moved to a new prototype
    return autoAssign(flagBranch, alreadyRedirected);
  }

  // No cookie
  return flagBranch ? autoAssign(flagBranch, alreadyRedirected) : NOTHING;
}

function autoAssign(branch: string, alreadyRedirected: boolean): AssignmentDecision {
  if (alreadyRedirected) {
    // The loop guard tripped (e.g. the branch's build is missing and the user hit Reset).
    // Stay put, but still offer the prototype.
    return { status: 'opted-out', action: { type: 'none' } };
  }
  return { status: 'opted-in', action: { type: 'assign', branch } };
}

/**
 * The loop guard lives in a browser-session cookie rather than sessionStorage: the branch cookie
 * is shared by every tab, so a per-tab guard let each new tab redirect (and fail) again.
 */
const REDIRECT_GUARD_COOKIE = 'gaia_prototype_redirects';

function readRedirectGuard(): string[] {
  const prefix = `${REDIRECT_GUARD_COOKIE}=`;
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith(prefix))
    ?.slice(prefix.length);
  if (!raw) return [];
  try {
    return raw.split(',').map(decodeURIComponent).filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Loop guard: whether this browser session already auto-redirected to `branch`.
 */
export function hasAutoRedirected(branch: string): boolean {
  return readRedirectGuard().includes(branch);
}

function recordAutoRedirect(branch: string): void {
  const branches = readRedirectGuard();
  if (!branches.includes(branch)) branches.push(branch);
  document.cookie = `${REDIRECT_GUARD_COOKIE}=${branches.map(encodeURIComponent).join(',')}; Path=/`;
}

function clearRedirectGuard(): void {
  document.cookie = `${REDIRECT_GUARD_COOKIE}=; Path=/; Max-Age=0`;
}

/**
 * On logout, forget this user's Labs state, so the next person on a shared browser starts from
 * the flag's value for them: no inherited prototype, opt-out or loop guard. Developer previews
 * (`?branch=`) are left alone.
 */
export function resetLabsAssignment(): void {
  const status = readBranchCookie()?.status;
  if (status === 'labs' || status === 'labsOptOut') clearBranchCookie();
  clearRedirectGuard();
}

/**
 * Carries out an automatic sync action.
 */
export function performAssignmentAction(action: AssignmentAction): void {
  switch (action.type) {
    case 'assign':
      recordAutoRedirect(action.branch);
      writeBranchCookie({ name: action.branch, status: 'labs' });
      reloadApp();
      return;
    case 'clear':
      clearBranchCookie();
      reloadApp();
      return;
    case 'none':
      return;
  }
}

/**
 * "Try it": opt into the prototype and reload onto it.
 */
export function optIntoPrototype(branch: string): void {
  writeBranchCookie({ name: branch, status: 'labs' });
  reloadApp();
}

/**
 * "Leave prototype": opt out and reload onto main.
 */
export function optOutOfPrototype(): void {
  writeBranchCookie({ name: '', status: 'labsOptOut' });
  reloadApp();
}
